import { useEnrollmentStatuses } from './useEnrollmentStatuses';
import { useNotificationWizard } from './useNotificationWizard';
import { useSelectionSummary } from './useSelectionSummary';

/**
 * Hook para gerenciar a etapa 1 do wizard — seleção de público.
 * Fornece os status de matrícula disponíveis (via API), o estado de seleção atual
 * e os handlers de interação. A lista de turmas é gerenciada internamente
 * pelo organismo `ClassroomList`.
 *
 * @returns Dados e handlers da etapa de seleção de público
 *
 * @example
 * ```tsx
 * const { enrollmentStatuses, selectedClassroomIds, toggleClassroom } = useNotificationAudience();
 * ```
 */
export function useNotificationAudience() {
  const { wizardState, updateAudience } = useNotificationWizard();
  const {
    classroomIds,
    enrollmentStatuses: selectedStatuses,
    classroomStudentCounts,
    classroomNames,
  } = wizardState.audience;

  const { enrollmentStatuses, isLoading: isLoadingStatuses } = useEnrollmentStatuses();

  const optimisticStudentCount = Object.values(classroomStudentCounts).reduce(
    (sum, count) => sum + count,
    0,
  );

  const { totalStudents, isLoading: isLoadingSummary } = useSelectionSummary(
    classroomIds,
    selectedStatuses,
  );

  const selectedStudentCount = totalStudents ?? optimisticStudentCount;

  const toggleClassroom = (id: string, studentCount: number, name: string) => {
    const isSelected = classroomIds.includes(id);

    if (isSelected) {
      const nextIds = classroomIds.filter(cId => cId !== id);
      const nextCounts = Object.fromEntries(
        Object.entries(classroomStudentCounts).filter(([key]) => key !== id),
      );
      const nextNames = Object.fromEntries(
        Object.entries(classroomNames).filter(([key]) => key !== id),
      );
      updateAudience({
        classroomIds: nextIds,
        classroomStudentCounts: nextCounts,
        classroomNames: nextNames,
      });
    } else {
      const nextIds = [...classroomIds, id];
      const nextCounts = { ...classroomStudentCounts, [id]: studentCount };
      const nextNames = { ...classroomNames, [id]: name };
      updateAudience({
        classroomIds: nextIds,
        classroomStudentCounts: nextCounts,
        classroomNames: nextNames,
      });
    }
  };

  const selectPageClassrooms = (
    pageClassrooms: ReadonlyArray<{ id: string; studentCount: number; name: string }>,
  ) => {
    const pageIds = pageClassrooms.map(c => c.id);
    const allSelected = pageIds.every(id => classroomIds.includes(id));

    if (allSelected) {
      const nextIds = classroomIds.filter(id => !pageIds.includes(id));
      const nextCounts = Object.fromEntries(
        Object.entries(classroomStudentCounts).filter(([id]) => !pageIds.includes(id)),
      );
      const nextNames = Object.fromEntries(
        Object.entries(classroomNames).filter(([id]) => !pageIds.includes(id)),
      );
      updateAudience({
        classroomIds: [...nextIds],
        classroomStudentCounts: nextCounts,
        classroomNames: nextNames,
      });
    } else {
      const nextIds = [...new Set([...classroomIds, ...pageIds])];
      const addedCounts = Object.fromEntries(pageClassrooms.map(c => [c.id, c.studentCount]));
      const addedNames = Object.fromEntries(pageClassrooms.map(c => [c.id, c.name]));
      updateAudience({
        classroomIds: nextIds,
        classroomStudentCounts: { ...classroomStudentCounts, ...addedCounts },
        classroomNames: { ...classroomNames, ...addedNames },
      });
    }
  };

  const toggleStatus = (id: string) => {
    const next = selectedStatuses.includes(id)
      ? selectedStatuses.filter(s => s !== id)
      : [...selectedStatuses, id];
    updateAudience({ enrollmentStatuses: next });
  };

  const toggleAllStatuses = () => {
    const allSelected =
      enrollmentStatuses.length > 0 &&
      enrollmentStatuses.every(s => selectedStatuses.includes(s.id));
    updateAudience({
      enrollmentStatuses: allSelected ? [] : enrollmentStatuses.map(s => s.id),
    });
  };

  return {
    enrollmentStatuses,
    isLoadingStatuses,
    isLoadingSummary,
    selectedClassroomIds: classroomIds,
    selectedClassroomNames: classroomNames,
    selectedStatuses,
    selectedStudentCount,
    toggleClassroom,
    selectPageClassrooms,
    toggleStatus,
    toggleAllStatuses,
  } as const;
}
